<!--
  @component ItemDetailsEdit
  @description Edit form for item details with dynamic field support per item type
-->
<script lang="ts">
  import { createEventDispatcher } from 'svelte';
  import { t } from '@/i18n';
  import {
    getAvailableItemTypes,
    getFieldsForItemType,
    getCreatorTypesForItemType,
    getDefaultCreatorType,
  } from '@/data';
  import CreatorEditor from './CreatorEditor.svelte';
  import type { Item, ItemField } from '@/types';

  export let editData: Partial<Item['data']>;
  export let originalDate: string;

  const dispatch = createEventDispatcher<{
    save: void;
    cancel: void;
    delete: void;
  }>();

  // Fields that should use textarea
  const textareaFields: ItemField[] = ['abstractNote', 'extra', 'history', 'references'];

  // Fields to exclude from dynamic rendering (handled separately)
  const excludedFields: ItemField[] = ['title', 'creators', 'extra'];

  // System fields that should always be preserved (not cleaned on type change)
  const systemFields = [
    'itemType',
    'key',
    'version',
    'dateAdded',
    'dateModified',
    'parentItem',
    'collections',
    'relations',
    'tags',
  ];

  // Available item types for dropdown
  $: itemTypeOptions = getAvailableItemTypes();

  // Get fields for the current item type
  $: currentFields = getFieldsForItemType(editData.itemType as string);

  // Get editable fields (excluding special ones)
  $: editableFields = currentFields.filter((field) => !excludedFields.includes(field));

  // Get field label
  function getFieldLabel(field: string): string {
    return $t(`item.fields.${field}`) || field;
  }

  // Check if field is a textarea field
  function isTextareaField(field: ItemField): boolean {
    return textareaFields.includes(field);
  }

  // Handle item type change - update creator types and clean invalid fields
  function handleItemTypeChange() {
    if (!editData) return;

    const newItemType = editData.itemType as string;
    const validFields = getFieldsForItemType(newItemType);
    const newValidCreatorTypes = getCreatorTypesForItemType(newItemType);
    const defaultCreatorType = getDefaultCreatorType(newItemType);

    // Update any invalid creator types to the default
    if (editData.creators) {
      editData.creators = editData.creators.map((creator) => {
        if (!newValidCreatorTypes.includes(creator.creatorType)) {
          return { ...creator, creatorType: defaultCreatorType };
        }
        return creator;
      });
    }

    // Clean up fields that are not valid for the new item type
    // This prevents 400 errors from the Zotero API
    const fieldsToCheck = Object.keys(editData) as (keyof typeof editData)[];
    for (const field of fieldsToCheck) {
      // Keep system fields and valid fields for the new type
      if (systemFields.includes(field) || validFields.includes(field as ItemField)) {
        continue;
      }
      // Remove invalid fields (set to undefined so they're not sent)
      delete editData[field];
    }

    // Trigger reactivity
    editData = editData;
  }

  function handleSave() {
    dispatch('save');
  }

  function handleCancel() {
    dispatch('cancel');
  }

  function handleDelete() {
    dispatch('delete');
  }
</script>

<!-- Item Type -->
<div class="field-group">
  <label class="field-label" for="edit-itemType">{$t('item.itemType')}</label>
  <select
    id="edit-itemType"
    class="field-select"
    bind:value={editData.itemType}
    on:change={handleItemTypeChange}
  >
    {#each itemTypeOptions as option}
      <option value={option.value}>{option.icon} {$t(option.labelKey)}</option>
    {/each}
  </select>
</div>

<!-- Title -->
<div class="field-group">
  <label class="field-label" for="edit-title">{getFieldLabel('title')}</label>
  <input id="edit-title" class="field-input" bind:value={editData.title} />
</div>

<!-- Creators -->
{#if editData.creators}
  <CreatorEditor bind:creators={editData.creators} itemType={editData.itemType || 'document'} />
{/if}

<!-- Original Date -->
<div class="field-group">
  <label class="field-label" for="edit-originalDate">{getFieldLabel('originalDate')}</label>
  <input
    id="edit-originalDate"
    class="field-input"
    bind:value={originalDate}
    placeholder="ex: 1859"
  />
</div>

<!-- Dynamic fields based on item type -->
{#each editableFields as field (field)}
  <div class="field-group">
    <label class="field-label" for="edit-{field}">{getFieldLabel(field)}</label>
    {#if isTextareaField(field)}
      <textarea id="edit-{field}" class="field-textarea" bind:value={editData[field]}></textarea>
    {:else}
      <input id="edit-{field}" class="field-input" bind:value={editData[field]} />
    {/if}
  </div>
{/each}

<!-- Extra -->
<div class="field-group">
  <label class="field-label" for="edit-extra">{getFieldLabel('extra')}</label>
  <textarea id="edit-extra" class="field-textarea small" bind:value={editData.extra}></textarea>
</div>

<!-- Edit Actions -->
<div class="action-buttons">
  <button class="action-button primary" on:click={handleSave}>
    {$t('common.save')}
  </button>
  <button class="action-button secondary" on:click={handleCancel}>
    {$t('common.cancel')}
  </button>
  <button class="action-button danger" on:click={handleDelete}>
    {$t('common.delete')}
  </button>
</div>

<style>
  .field-group {
    margin-bottom: 16px;
    padding-bottom: 12px;
    border-bottom: 1px solid var(--color-border-light, #444);
  }

  .field-group:last-of-type:not(.action-buttons) {
    border-bottom: none;
  }

  .field-label {
    display: block;
    font-size: 12px;
    font-weight: 600;
    color: var(--color-text-secondary, #bbb);
    margin-bottom: 4px;
  }

  .field-input,
  .field-select {
    width: 100%;
    padding: 8px;
    background: var(--color-bg-primary, #2b2b2b);
    border: 1px solid var(--color-border, #555);
    border-radius: 4px;
    color: var(--color-text-primary, #e8e8e8);
    font-size: 13px;
    box-sizing: border-box;
    transition: border-color 0.2s;
  }

  .field-input:focus,
  .field-select:focus {
    outline: none;
    border-color: var(--color-accent, #5c7cfa);
  }

  .field-textarea {
    width: 100%;
    padding: 8px;
    background: var(--color-bg-primary, #2b2b2b);
    border: 1px solid var(--color-border, #555);
    border-radius: 4px;
    color: var(--color-text-primary, #e8e8e8);
    font-size: 13px;
    box-sizing: border-box;
    min-height: 100px;
    resize: vertical;
    font-family: inherit;
    transition: border-color 0.2s;
  }

  .field-textarea.small {
    min-height: 60px;
  }

  .field-textarea:focus {
    outline: none;
    border-color: var(--color-accent, #5c7cfa);
  }

  .action-buttons {
    display: flex;
    gap: 8px;
    margin-top: 16px;
    flex-wrap: wrap;
    border-bottom: none !important;
  }

  .action-button {
    flex: 1;
    min-width: 80px;
    padding: 8px 12px;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-size: 12px;
    font-weight: 600;
    transition: background-color 0.2s;
  }

  .action-button.primary {
    background: var(--color-accent, #5c7cfa);
    color: white;
  }

  .action-button.primary:hover {
    background: var(--color-accent-hover, #4c6ef5);
  }

  .action-button.secondary {
    background: var(--color-bg-tertiary, #666);
    color: white;
  }

  .action-button.secondary:hover {
    background: var(--color-bg-hover, #777);
  }

  .action-button.danger {
    background: var(--color-danger, #dc3545);
    color: white;
  }

  .action-button.danger:hover {
    background: var(--color-danger-hover, #c82333);
  }
</style>
